import React, { FC, useRef } from "react";
import { Box, Link, Menu, Slide, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";

import Logo from "@components/Logo";
import { MenuLink } from "@src/types";
import { Colors } from "@theme/colors";
import TopNav from "./topnav";

import MobileNav from "./mobile";

import {
  uiContainerTransitionMs,
  uiOpacityHover,
  useIntersection,
} from "@src/utils";
import { ContainedSignup } from "./buttons";
import { addUrlParams, consoleLoginURL } from "@src/urls";

interface Props {
  isHome?: boolean;
  reverseColor?: boolean;
  noBackground?: boolean;
  menuLinks: Array<MenuLink>;
  maxWidth?: number;
  pageContext?: any;
}

const Header: FC<Props> = ({
  isHome = false,
  reverseColor = false,
  noBackground = false,
  menuLinks,
  maxWidth = 1380,
  pageContext = {},
}) => {
  const theme = useTheme();
  //const showNav = useMediaQuery(theme.breakpoints.up("md"));
  const showNav = useMediaQuery("(min-width:950px)");
  const isSmall = useMediaQuery(theme.breakpoints.up("sm"));
  const header = useRef<HTMLElement>();

  const logoColor =
    isHome || reverseColor ? Colors.PrimaryContrast : Colors.PrimaryMain;

  let imageUrl = "/backgrounds/homepage-bg-600.svg";

  if (showNav) {
    imageUrl = "/backgrounds/homepage-bg-full.svg";
  } else if (isSmall) {
    imageUrl = "/backgrounds/homepage-bg-900.png";
  }

  const loginUrl = addUrlParams(consoleLoginURL);

  const inViewport = useIntersection(header, "0px");

  // state and click handlers for mobile nav
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {isHome && (
        <Box
          sx={{
            position: "absolute",
            top: "0",
            width: { xs: "100%", md: "calc(100vw + 1150px)" },
            height: "1200px",
            zIndex: "1",
            background: {
              xs: "url('/backgrounds/homepage-bg-600.png') no-repeat top, linear-gradient(#4d196d 6%, transparent 6%, transparent)",
              sm: "url('/backgrounds/homepage-bg-900.png') no-repeat top, linear-gradient(#4d196d 6%, transparent 6%, transparent)",
              md: "url('/backgrounds/homepage-bg-full.svg') no-repeat top",
            },
            backgroundPositionX: "center",
          }}
        >
          {/* Attempt to load bg image sooner */}
          <img
            src={imageUrl}
            style={{ display: "none" }}
            alt="header image preloader"
          />
        </Box>
      )}
      {/* Sticky header */}
      {showNav && (
        <Slide direction="down" in={!inViewport} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              top: "0",
              width: "100%",
              height: "68px",
              margin: "0 auto",
              backgroundColor: "#3b1253",
              borderBottom: "1px inset rgba(255,255,255,0.3)",
              zIndex: "5",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                height: "100%",
                maxWidth: "1380px",
                margin: "0 auto",
                padding: { xs: "0 24px", lg: "0 48px" },
              }}
            >
              <Link
                href="/"
                sx={{
                  height: "15px",
                  color: `${Colors.PrimaryContrast} !important`,
                  opacity: 1,
                  transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
                  "&:hover": {
                    opacity: uiOpacityHover,
                    color: `${Colors.PrimaryContrast} !important`,
                  },
                }}
              >
                <Logo />
              </Link>
              <TopNav
                isHome={true}
                reverseColor={false}
                menuLinks={menuLinks}
                pageContext={pageContext}
              />
            </Stack>
          </Box>
        </Slide>
      )}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: `${maxWidth}px`,
          height: "68px",
          margin: "0 auto",
          zIndex: "5",
          background: isHome || noBackground ? "none" : "#FFFFFF",
        }}
      >
        <Stack
          {...({ ref: header } as any)}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            height: "100%",
            maxWidth: `${maxWidth}px`,
            margin: "0 auto",
            padding: { xs: "0 24px", lg: "0 48px" },
          }}
        >
          <Link
            sx={{
              height: "15px",
              color: `${logoColor} !important`,
              opacity: 1,
              transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
              transform: `translateZ(0) scale(1, 1)`,
              "&:hover": {
                opacity: uiOpacityHover,
                color: `${logoColor} !important`,
              },
            }}
            href="/"
          >
            <Logo />
          </Link>
          {showNav && inViewport && (
            <TopNav
              isHome={isHome}
              reverseColor={reverseColor}
              menuLinks={menuLinks}
              pageContext={pageContext}
            />
          )}
          {!showNav && (
            <>
              <IconButton onClick={handleClick}>
                <DensityMediumIcon
                  sx={{
                    color:
                      isHome || reverseColor
                        ? Colors.PrimaryContrast
                        : Colors.PrimaryMain,
                  }}
                />
              </IconButton>
              <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
                <Box
                  sx={{
                    minWidth: "335px",
                    padding: "16px 24px 0",
                    borderRadius: "24px",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    mb="12px"
                  >
                    <ContainedSignup
                      url={loginUrl}
                      bgColor={Colors.PrimaryMain}
                      textColor={Colors.PrimaryContrast}
                    >
                      Log In
                    </ContainedSignup>

                    <IconButton onClick={handleClose}>
                      <CloseIcon sx={{ color: Colors.HeadingText }} />
                    </IconButton>
                  </Stack>
                  <>
                    {menuLinks.map((link) => (
                      <MobileNav key={link.name} link={link} />
                    ))}

                    <MobileNav
                      link={{
                        name: "Contact Us",
                        link: "/contact-us/",
                        type: "internal",
                        icon: "",
                      }}
                    />
                  </>
                </Box>
              </Menu>
            </>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default Header;
